"use client";

/**
 * ChatOutline — Xynth turn navigator
 * ----------------------------------
 * A slim desktop rail plus a compact mobile sheet for jumping through long
 * chats. It observes existing message DOM anchors, so it does not touch the
 * stream parser, message state, or backend protocol.
 *
 * Design notes:
 * - Reads turns from a shared store (outline-store) that the active Messages /
 *   ArtifactMessages component publishes from its in-memory `messages` array.
 *   The chat list is virtualized (react-virtuoso) so off-screen messages are
 *   NOT in the DOM — reading the DOM only ever saw the few mounted near the
 *   viewport, which made the timeline appear to "fill in" while scrolling.
 * - Jumps go through Virtuoso's scrollToIndex (registered in the store) so we
 *   can scroll to a turn even when its DOM node is currently unmounted.
 * - Component is ALWAYS mounted (returns null when there are no turns yet).
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ListTree, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useSidebar } from "@/components/ui/sidebar";
import { useArtifact } from "@/hooks/use-artifact";
import { telemetry } from "@/lib/telemetry";
import { cn } from "@/lib/utils";
import {
  getOutlineServerSnapshot,
  getOutlineTurns,
  jumpToOutlineIndex,
  subscribeOutline,
  type OutlineTurn,
} from "./outline-store";

const COLLAPSED_WIDTH = 32;
const EXPANDED_WIDTH = 300;
const FIRST_RUN_KEY = "chatOutline:seenTooltip";

function flashTarget(el: HTMLElement) {
  const bubble =
    el.querySelector<HTMLElement>('[data-testid="message-content"]') ??
    (el.firstElementChild as HTMLElement | null) ??
    el;
  const prev = bubble.style.transition;
  bubble.style.transition = "box-shadow 240ms ease-out";
  bubble.style.boxShadow = "0 0 0 1px rgba(56,189,248,0.65)";
  window.setTimeout(() => {
    bubble.style.boxShadow = "0 0 0 0 rgba(56,189,248,0)";
  }, 900);
  window.setTimeout(() => {
    bubble.style.boxShadow = "";
    bubble.style.transition = prev;
  }, 1500);
}

export function ChatOutline() {
  const { state, isMobile } = useSidebar();
  const pathname = usePathname();
  const { artifact } = useArtifact();
  const turns = useSyncExternalStore(
    subscribeOutline,
    getOutlineTurns,
    getOutlineServerSnapshot,
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showFirstRunTip, setShowFirstRunTip] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const telemetrySeenRef = useRef<Set<string>>(new Set());
  // While a click-driven smooth scroll is animating, the IntersectionObserver
  // would otherwise walk `activeId` through every message the page passes,
  // making the highlighted marker flicker/jump. We freeze IO-driven updates
  // until this timestamp so the clicked marker stays selected.
  const suppressIOUntilRef = useRef(0);

  // Active-turn tracking via IntersectionObserver scoped to the chat scroller.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (turns.length === 0) return;
    const scrollRoot = document.querySelector<HTMLElement>("[data-chat-messages]");
    // Persistent map of how visible each message currently is. IO callbacks
    // only report the messages that CHANGED, so we accumulate ratios here and
    // always pick the winner across the full set — this prevents the active
    // marker from flickering between messages on partial scrolls.
    const ratios = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).getAttribute("data-message-id");
          if (!id) continue;
          if (e.isIntersecting) ratios.set(id, e.intersectionRatio);
          else ratios.delete(id);
        }
        // Don't fight a click-driven smooth scroll: keep the clicked marker
        // selected until the animation settles.
        if (Date.now() < suppressIOUntilRef.current) return;
        let bestId: string | null = null;
        let bestRatio = -1;
        ratios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActiveId(bestId);
      },
      { root: scrollRoot ?? null, threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1] }
    );
    turns.forEach((t) => {
      const el = document.getElementById(`msg-${t.id}`);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [turns]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = window.localStorage.getItem(FIRST_RUN_KEY);
    if (!seen && turns.length >= 2) {
      setShowFirstRunTip(true);
      const t = window.setTimeout(() => setShowFirstRunTip(false), 5000);
      return () => window.clearTimeout(t);
    }
  }, [turns.length]);

  const dismissFirstRun = useCallback(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(FIRST_RUN_KEY, "1");
    setShowFirstRunTip(false);
  }, []);

  const scrollToTurn = useCallback(
    (id: string) => {
      const turn = turns.find((item) => item.id === id);
      if (!turn) return;
      // Freeze IO-driven active updates for the duration of the smooth scroll
      // so the marker we just clicked doesn't get overwritten as the page
      // animates past intermediate messages.
      suppressIOUntilRef.current = Date.now() + 900;
      // Jump via Virtuoso — the target message may be unmounted (virtualized),
      // so getElementById/scrollIntoView would no-op. scrollToIndex mounts it.
      jumpToOutlineIndex(turn.index);
      setActiveId(id);
      setIsMobileOpen(false);
      dismissFirstRun();
      telemetry.emit("chat.timeline_jump", {
        message_id: id,
        role: turn.role,
        turn_index: turn.index,
        surface: isMobile ? "mobile_sheet" : "desktop_rail",
        marker_count: turns.length,
      });
      // Flash once Virtuoso has had time to mount + scroll the target in.
      window.setTimeout(() => {
        const el = document.getElementById(`msg-${id}`);
        if (el) flashTarget(el);
      }, 420);
    },
    [dismissFirstRun, isMobile, turns]
  );

  const userTurnCount = useMemo(
    () => turns.filter((turn) => turn.role === "user").length,
    [turns],
  );

  const emitOpenTelemetry = useCallback(
    (surface: "desktop_rail" | "mobile_sheet") => {
      const key = `${surface}:${turns.length}`;
      if (telemetrySeenRef.current.has(key)) return;
      telemetrySeenRef.current.add(key);
      telemetry.emit("chat.timeline_opened", {
        surface,
        marker_count: turns.length,
        user_turn_count: userTurnCount,
      });
    },
    [turns.length, userTurnCount],
  );

  const isAlertsDetailPage = pathname?.startsWith("/alerts/");
  const shouldHideForCollapsedAlertChat =
    isAlertsDetailPage && Boolean(artifact.isChatMinimized);

  if (turns.length === 0 || shouldHideForCollapsedAlertChat) return null;

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          data-testid="chat-outline-mobile-trigger"
          onClick={() => {
            setIsMobileOpen(true);
            emitOpenTelemetry("mobile_sheet");
          }}
          className={cn(
            "fixed right-0 top-1/2 z-40 -translate-y-1/2 inline-flex flex-col items-center gap-0.5",
            "rounded-l-lg border border-r-0 border-white/10 bg-zinc-950/95 px-1.5 py-2.5",
            "text-[10px] font-semibold tabular-nums text-zinc-300",
            "shadow-lg shadow-black/40 backdrop-blur-md active:scale-[0.97]",
          )}
          aria-label={`Open chat timeline (${userTurnCount || turns.length} turns)`}
        >
          <ListTree className="size-4 text-sky-300/90" strokeWidth={1.8} />
          <span>{userTurnCount || turns.length}</span>
        </button>

        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              className="fixed inset-0 z-[210] flex items-center justify-center p-4 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                type="button"
                className="absolute inset-0 bg-black/70"
                aria-label="Close chat timeline"
                onClick={() => setIsMobileOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                className="relative flex max-h-[80dvh] w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl"
              >
                <div className="flex shrink-0 items-center justify-between border-b border-white/5 px-4 py-2.5">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-300/90">
                      Timeline
                    </div>
                    <div className="mt-0.5 text-[11px] text-zinc-500">
                      {turns.length} turns · tap to jump
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsMobileOpen(false)}
                    className="grid size-8 place-items-center rounded-md border border-white/10 bg-white/[0.02] text-zinc-400"
                    aria-label="Close chat timeline"
                  >
                    <X className="size-3.5" strokeWidth={1.8} />
                  </button>
                </div>
                <TurnList
                  turns={turns}
                  activeId={activeId}
                  expanded
                  onSelect={scrollToTurn}
                  variant="sheet"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  const width = isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
  const leftOffset =
    state === "expanded" ? "var(--sidebar-width, 0px)" : "0px";

  return (
    <>
      {/* Flex-flow spacer that reserves COLLAPSED_WIDTH inside the
          SidebarProvider grid so `<SidebarInset>` content is pushed
          right by the collapsed rail's width. Without this the rail
          renders as `position: fixed` and overlays the chat / artifact
          content (covering text on the left edge — visible on the
          alert canvas and notebook open states). The hover-expanded
          rail (300px) still overlays via the fixed positioning below
          since expansion is user-initiated and transient. */}
      <div
        aria-hidden
        className="hidden md:block shrink-0"
        style={{ width: COLLAPSED_WIDTH }}
      />
    <div
      ref={containerRef}
      data-testid="chat-outline"
      onMouseEnter={() => {
        setIsHovered(true);
        emitOpenTelemetry("desktop_rail");
      }}
      onMouseLeave={() => setIsHovered(false)}
      className="fixed top-[var(--referral-banner-h,0px)] bottom-0 z-20 hidden md:flex transition-[left] duration-200 ease-linear"
      style={{ left: leftOffset }}
    >
      {/* Content-hugging floating capsule: height grows with the number of
          turns instead of stretching to the full viewport, so there is no
          dead black column under a short chat. Long chats cap at the
          viewport and scroll internally. */}
      <div className="flex h-full flex-col justify-center py-[60px]">
        <motion.div
          animate={{ width }}
          transition={{ duration: 0.16, ease: "easeOut" }}
          className={cn(
            "ml-1 max-h-[calc(100dvh-140px)] overflow-hidden rounded-2xl transition-colors",
            isHovered
              ? "border border-white/10 bg-zinc-950/95 shadow-xl shadow-black/50 backdrop-blur-md"
              : "border border-white/[0.05] bg-zinc-950/40 backdrop-blur-sm",
          )}
        >
          <div className="flex max-h-[calc(100dvh-140px)] flex-col">
            {isHovered && (
              <div className="flex shrink-0 items-center gap-2 border-b border-white/[0.06] px-3 py-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300/80">
                  Timeline
                </span>
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-px text-[10px] font-medium tabular-nums text-zinc-400">
                  {turns.length}
                </span>
              </div>
            )}
            <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              <TurnList
                turns={turns}
                activeId={activeId}
                expanded={isHovered}
                onSelect={scrollToTurn}
                variant="rail"
              />
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showFirstRunTip && !isHovered && (
          <motion.div
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute left-[44px] top-1/2 z-30 max-w-[220px] -translate-y-1/2 rounded-md border border-sky-300/25 bg-zinc-950/95 px-3 py-2 shadow-lg"
          >
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300/90">
              Timeline
            </div>
            <div className="text-[11.5px] leading-snug text-zinc-300">
              Hover the rail to see every turn. Click any marker to jump.
            </div>
            <button
              type="button"
              onClick={dismissFirstRun}
              className="mt-1.5 text-[10.5px] text-zinc-500 hover:text-zinc-300"
            >
              Got it
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

function TurnList({
  turns,
  activeId,
  expanded,
  onSelect,
  variant,
}: {
  turns: OutlineTurn[];
  activeId: string | null;
  expanded: boolean;
  onSelect: (id: string) => void;
  variant: "rail" | "sheet";
}) {
  return (
    <div className="relative">
      {/* Vertical timeline spine. Only visible when the row is wide enough
          to keep alignment correct. */}
      {expanded && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-2 left-[19px] w-px bg-white/[0.06]"
        />
      )}
      <ul
        className={cn(
          "flex flex-col overflow-y-auto",
          variant === "rail"
            ? "max-h-[calc(100dvh-120px)] gap-px py-1.5"
            : "max-h-[64dvh] gap-px py-1.5",
          expanded ? "px-2" : "px-1.5",
        )}
      >
        {turns.map((turn) => {
          const isActive = activeId === turn.id;
          const isUser = turn.role === "user";
          return (
            <li key={turn.id}>
              <button
                type="button"
                onClick={() => onSelect(turn.id)}
                aria-label={`Jump to ${isUser ? "your message" : "Xynth response"} ${turn.index + 1}: ${turn.label}`}
                title={!expanded ? turn.label : undefined}
                className={cn(
                  "group relative flex w-full items-start gap-2.5 border-l-2 border-transparent transition-colors",
                  "text-left",
                  expanded ? "px-2 py-1.5" : "justify-center px-0 py-1.5",
                  isActive
                    ? "border-l-sky-300 bg-white/[0.04]"
                    : "hover:border-l-white/20 hover:bg-white/[0.025]",
                )}
              >
                {/* Spine dot — replaces the "YOU/AI" pill with a cleaner
                    indicator. Users get a hollow white ring, Xynth gets a
                    filled sky dot. The active state intensifies both. */}
                <span
                  aria-hidden
                  className={cn(
                    "relative mt-[5px] grid size-3 shrink-0 place-items-center rounded-full transition-all",
                  )}
                >
                  <span
                    className={cn(
                      "absolute inset-0 rounded-full border transition-all",
                      isUser
                        ? isActive
                          ? "border-white/80"
                          : "border-white/30 group-hover:border-white/55"
                        : isActive
                          ? "border-sky-300/80 bg-sky-300/30"
                          : "border-sky-300/30 bg-sky-300/10 group-hover:border-sky-300/55",
                    )}
                  />
                  <span
                    className={cn(
                      "size-[5px] rounded-full transition-all",
                      isUser
                        ? isActive
                          ? "bg-white"
                          : "bg-white/40 group-hover:bg-white/65"
                        : isActive
                          ? "bg-sky-300"
                          : "bg-sky-300/60 group-hover:bg-sky-300/85",
                    )}
                  />
                </span>

                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      initial={{ opacity: 0, x: -3 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -3 }}
                      transition={{ duration: 0.1 }}
                      className="flex min-w-0 flex-1 flex-col gap-0.5"
                    >
                      <span
                        className={cn(
                          "text-[9.5px] font-semibold uppercase tracking-[0.16em] transition-colors",
                          isUser
                            ? isActive
                              ? "text-white/90"
                              : "text-zinc-500 group-hover:text-white/75"
                            : isActive
                              ? "text-sky-300"
                              : "text-zinc-500 group-hover:text-sky-300/85",
                        )}
                      >
                        {isUser ? "You" : "Xynth"}
                      </span>
                      <span
                        className={cn(
                          "min-w-0 text-[12px] leading-snug line-clamp-2 transition-colors",
                          isActive
                            ? "text-zinc-100"
                            : "text-zinc-400 group-hover:text-zinc-200",
                        )}
                      >
                        {turn.label}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
